import * as React from 'react'
import type { ErrorBaseProps } from '../errors/error-overlay/error-overlay'
import type { ErrorOverlayTabBarRenderer } from '../errors/error-overlay-pagination/error-overlay-pagination'
import { ErrorOverlayNav } from '../errors/error-overlay-nav/error-overlay-nav'
import { ErrorOverlayOverlay } from '../errors/overlay/overlay'
import { ErrorOverlayDialog } from '../errors/dialog/dialog'
import { ErrorOverlayDialogHeader } from '../errors/dialog/header'
import { ErrorOverlayDialogBody } from '../errors/dialog/body'
import { OverlayBackdrop } from '../overlay'
import { DialogContent } from '../dialog'
import { Resizer } from '../resizer'
import { useFocusTrap } from '../errors/dev-tools-indicator/utils'
import { CopyButton } from '../copy-button'
import { CodeFrameShell } from '../code-frame/code-frame-shell'
import { CopyPromptIcon } from '../../icons/copy-prompt'
import { css } from '../../utils/css'

export function VulnerabilityInsight({
  renderTabBar,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  onClose,
  rendered,
  transitionDurationMs,
  versionInfo,
}: ErrorBaseProps & {
  renderTabBar: ErrorOverlayTabBarRenderer | undefined
  canGoPrevious: boolean
  canGoNext: boolean
  onPrevious: (() => void) | undefined
  onNext: (() => void) | undefined
  onClose: (() => void) | undefined
}) {
  const prompt =
    'Run `next upgrade --agent=security` and follow its instructions.'
  const animationProps = {
    'data-rendered': rendered,
    style: {
      '--transition-duration': `${transitionDurationMs}ms`,
    } as React.CSSProperties,
  }
  const [animating, setAnimating] = React.useState(
    Boolean(transitionDurationMs)
  )
  const dialogRef = React.useRef<HTMLDivElement | null>(null)
  useFocusTrap(dialogRef, null, rendered)

  function onTransitionEnd({ propertyName, target }: React.TransitionEvent) {
    if (propertyName === 'scale' && target === dialogRef.current) {
      setAnimating(false)
    }
  }

  return (
    <ErrorOverlayOverlay {...animationProps}>
      <OverlayBackdrop fixed={false} />
      <div
        data-nextjs-dialog-root
        onTransitionEnd={onTransitionEnd}
        ref={dialogRef}
        {...animationProps}
      >
        <ErrorOverlayNav
          versionInfo={versionInfo}
          renderTabBar={renderTabBar}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onPrevious={onPrevious}
          onNext={onNext}
        />
        <ErrorOverlayDialog onClose={onClose}>
          <Resizer measure={!animating} data-nextjs-dialog-sizer>
            <DialogContent>
              <ErrorOverlayDialogHeader>
                <div data-nextjs-vulnerability-insight>
                  <span
                    id="nextjs__container_errors_label"
                    className="nextjs__container_errors_label nextjs__container_errors_label_instant"
                    data-nextjs-vulnerability-label
                  >
                    Security Insight
                  </span>
                  <h2
                    id="nextjs__container_errors_desc"
                    className="nextjs__container_errors_desc nextjs__container_errors_desc_instant"
                  >
                    Upgrade Next.js to a safe version.
                  </h2>
                  <p>
                    Next.js {versionInfo.installed} is affected by a known
                    security vulnerability. We strongly recommend upgrading.
                  </p>
                </div>
              </ErrorOverlayDialogHeader>
              <ErrorOverlayDialogBody>
                <div data-nextjs-vulnerability-action>
                  <p>Ask your coding agent to upgrade:</p>
                  <CodeFrameShell
                    header={
                      <>
                        <span data-nextjs-vulnerability-prompt-title>
                          Agent prompt
                        </span>
                        <CopyButton
                          content={prompt}
                          actionLabel="Copy prompt"
                          successLabel="Copied"
                          icon={<CopyPromptIcon />}
                          showLabel
                          data-nextjs-vulnerability-copy
                        />
                      </>
                    }
                  >
                    <span data-nextjs-vulnerability-prompt>{prompt}</span>
                  </CodeFrameShell>
                </div>
              </ErrorOverlayDialogBody>
            </DialogContent>
          </Resizer>
        </ErrorOverlayDialog>
      </div>
      <style>{styles}</style>
    </ErrorOverlayOverlay>
  )
}

const styles = css`
  [data-nextjs-dialog-header]:has([data-nextjs-vulnerability-insight]) {
    padding-bottom: 0;
  }
  [data-nextjs-vulnerability-insight] h2 {
    margin: 16px 0 8px;
  }
  [data-nextjs-vulnerability-insight] p,
  [data-nextjs-vulnerability-action] > p {
    margin: 8px 0;
    font-size: var(--size-14);
    line-height: var(--size-20);
    font-family: var(--font-stack-sans);
    letter-spacing: normal;
    color: var(--color-gray-900);
  }
  [data-nextjs-vulnerability-action] {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  [data-nextjs-vulnerability-action] > p {
    margin-bottom: 0;
  }
  [data-nextjs-vulnerability-action] .code-frame-lines {
    min-width: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  [data-nextjs-vulnerability-action] [data-nextjs-vulnerability-prompt],
  [data-nextjs-vulnerability-action] [data-nextjs-vulnerability-prompt-title] {
    font-family: var(--font-stack-sans);
    font-size: var(--size-14);
    line-height: var(--size-20);
    letter-spacing: normal;
  }
  [data-nextjs-vulnerability-prompt-title] {
    font-size: var(--size-13);
  }
  [data-nextjs-vulnerability-copy] {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    gap: 6px;
    height: auto;
    padding: 5px 10px;
    border: 1px solid var(--color-gray-alpha-300);
    border-radius: 9999px;
    background: var(--color-background-100);
    color: var(--color-gray-900);
    font-family: var(--font-stack-sans);
    font-size: var(--size-11);
    font-weight: 500;
    line-height: 1;
    cursor: pointer;
    transition:
      background 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  [data-nextjs-vulnerability-action] [data-nextjs-copy-button-label] {
    font-family: var(--font-stack-sans);
    font-size: var(--size-11);
    line-height: 1;
  }
  [data-nextjs-vulnerability-copy] svg {
    width: var(--size-12);
    height: var(--size-12);
  }
  [data-nextjs-vulnerability-copy]:hover {
    background: var(--color-background-200);
    border-color: var(--color-gray-alpha-500);
    color: var(--color-gray-1000);
  }
  [data-nextjs-vulnerability-copy]:focus-visible {
    outline: var(--focus-ring);
    outline-offset: 2px;
  }
  [data-nextjs-vulnerability-copy][aria-disabled='true'] {
    opacity: 0.5;
  }
`
